import React, { useState } from "react";
import { format } from "date-fns";
import axios from "axios";
import { CalendarIcon, Moon, Check, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

import StarField from "../components/StarField";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SERVICES, TIME_SLOTS, COMPANY } from "../mock/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: "Service" },
  { id: 2, label: "Date & Time" },
  { id: 3, label: "Your Details" },
  { id: 4, label: "Confirm" },
];

const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service: "",
    date: null,
    times: [],
    name: "",
    email: "",
    phone: "",
    participantType: "",
    notes: "",
  });
  const [done, setDone] = useState(false);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Click-range time selection:
  // - First click: selects that slot.
  // - Second click on a different slot: fills in every hour between (inclusive).
  // - Click on already-selected slot while range exists: resets to that single slot.
  const handleTimeClick = (slot) => {
    const idx = TIME_SLOTS.indexOf(slot);
    setForm((prev) => {
      const selectedIdx = prev.times.map((t) => TIME_SLOTS.indexOf(t)).sort((a, b) => a - b);
      // Starting fresh
      if (prev.times.length === 0) {
        return { ...prev, times: [slot] };
      }
      // Exactly one anchor → create range between anchor and click
      if (prev.times.length === 1) {
        const anchor = selectedIdx[0];
        if (idx === anchor) return { ...prev, times: [] }; // deselect
        const [lo, hi] = idx < anchor ? [idx, anchor] : [anchor, idx];
        return { ...prev, times: TIME_SLOTS.slice(lo, hi + 1) };
      }
      // Range exists → reset to new single click
      return { ...prev, times: [slot] };
    });
  };

  const canNext = () => {
    if (step === 1) return !!form.service;
    if (step === 2) return !!form.date && form.times.length >= 2;
    if (step === 3) return form.name && form.email && form.phone && form.participantType;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        service: form.service,
        date: format(form.date, "yyyy-MM-dd"),
        times: form.times,
        name: form.name,
        email: form.email,
        phone: form.phone,
        participant_type: form.participantType,
        notes: form.notes,
      });
      setDone(true);
      toast.success("Booking request received!", {
        description: "We'll be in touch within 2 business hours.",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Please try again or call 0424 497 770.";
      toast.error("Something went wrong", { description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  const formattedTimeRange = () => {
    if (form.times.length === 0) return "";
    if (form.times.length === 1) return form.times[0];
    return `${form.times[0]} – ${form.times[form.times.length - 1]} (${form.times.length} hrs)`;
  };

  if (done) {
    return (
      <div className="relative min-h-[70vh]" data-testid="booking-success">
        <StarField count={40} />
        <div className="container-lux py-28 md:py-36 relative max-w-2xl">
          <div className="lux-card rounded-[2rem] p-12 md:p-16 text-center">
            <div className="w-20 h-20 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto mb-6 glow-pulse">
              <Check className="w-10 h-10 text-gold" />
            </div>
            <div className="divider-gold mb-5">Booking Confirmed</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-6">
              Thank you, <em className="text-gold not-italic">{form.name.split(" ")[0] || "friend"}.</em>
            </h2>
            <p className="text-white/70 mb-8 leading-relaxed">
              We've received your booking request for <strong className="text-white">{form.service}</strong> on <strong className="text-white">{form.date && format(form.date, "EEEE, MMMM do")}</strong> from <strong className="text-white">{formattedTimeRange()}</strong>. A member of our team will confirm your appointment within 2 business hours.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href={`tel:${COMPANY.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase text-gold hover:bg-gold/10 transition-all">
                Call {COMPANY.phone}
              </a>
              <button
                onClick={() => {
                  setDone(false);
                  setStep(1);
                  setForm({ service: "", date: null, times: [], name: "", email: "", phone: "", participantType: "", notes: "" });
                }}
                className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-white/70 hover:text-gold"
                data-testid="booking-reset-btn"
              >
                Book Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="booking-page">
      {/* Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <StarField count={40} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,165,116,0.1),transparent_60%)]" />
        <div className="container-lux relative z-10 max-w-3xl fade-up text-center">
          <div className="divider-gold mb-6 mx-auto">Book a Consultation</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] mb-6">
            Let's find the <em className="text-gold not-italic">right time</em> to talk.
          </h1>
          <p className="text-white/70 leading-relaxed">
            Our consultations are free, relaxed, and commitment-free. Takes about 15 minutes.
          </p>
        </div>
      </section>

      {/* Stepper + form */}
      <section className="pb-28 md:pb-36">
        <div className="container-lux max-w-3xl">
          {/* Steps */}
          <div className="flex items-center justify-between mb-12" data-testid="booking-stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border transition-all ${
                      step >= s.id
                        ? "bg-gold text-deep border-gold"
                        : "bg-transparent text-white/40 border-white/20"
                    }`}
                  >
                    {step > s.id ? <Check className="w-4 h-4" /> : <span className="font-serif">{s.id}</span>}
                  </div>
                  <div className={`text-[10px] tracking-[0.2em] uppercase ${step >= s.id ? "text-gold" : "text-white/40"}`}>
                    {s.label}
                  </div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-px mx-4 ${step > s.id ? "bg-gold" : "bg-white/10"}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step content */}
          <div className="lux-card rounded-[2rem] p-8 md:p-12">
            {step === 1 && (
              <div data-testid="step-service">
                <h2 className="font-serif text-3xl md:text-4xl mb-3">Which service interests you?</h2>
                <p className="text-white/60 mb-8">You can select one - we'll chat about the full picture later.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SERVICES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => updateForm({ service: s.title })}
                      className={`text-left p-5 rounded-2xl border transition-all ${
                        form.service === s.title
                          ? "border-gold bg-gold/10"
                          : "border-white/10 hover:border-gold/40 bg-deep-surface"
                      }`}
                      data-testid={`service-option-${s.id}`}
                    >
                      <div className="font-serif text-lg text-white mb-1">{s.title}</div>
                      <div className="text-xs text-white/50">{s.tagline}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-datetime">
                <h2 className="font-serif text-3xl md:text-4xl mb-3">When works best?</h2>
                <p className="text-white/60 mb-8">Pick a date and we'll offer available slots.</p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-3 block">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="w-full text-left flex items-center justify-between bg-deep-surface border border-white/10 rounded-xl px-5 py-4 text-white hover:border-gold/40 transition-colors"
                          data-testid="date-picker-trigger"
                        >
                          <span>{form.date ? format(form.date, "PPP") : "Select a date"}</span>
                          <CalendarIcon className="w-4 h-4 text-gold" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-deep-elevated border-white/10" align="start">
                        <Calendar
                          mode="single"
                          selected={form.date}
                          onSelect={(d) => updateForm({ date: d })}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                          className="text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-xs tracking-[0.2em] uppercase text-gold block">
                        Hours
                        {form.times.length > 0 && (
                          <span className="ml-3 text-gold-light normal-case tracking-normal">
                            · {form.times.length} hr{form.times.length > 1 ? "s" : ""} selected
                          </span>
                        )}
                      </Label>
                      {form.times.length > 0 && (
                        <button
                          type="button"
                          onClick={() => updateForm({ times: [] })}
                          className="text-[10px] tracking-[0.15em] uppercase text-white/50 hover:text-gold transition-colors"
                          data-testid="time-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-white/45 mb-3 italic">
                      2-hour minimum. Click a start hour, then an end hour - we'll fill everything in between.
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {TIME_SLOTS.map((slot) => {
                        const selected = form.times.includes(slot);
                        const isStart = selected && slot === form.times[0];
                        const isEnd = selected && slot === form.times[form.times.length - 1] && form.times.length > 1;
                        return (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => handleTimeClick(slot)}
                            className={`py-3 px-2 rounded-xl text-xs border transition-all relative ${
                              selected
                                ? "border-gold bg-gold/15 text-gold"
                                : "border-white/10 text-white/70 hover:border-gold/40"
                            }`}
                            data-testid={`time-${slot.replace(/[^a-z0-9]/gi, "")}`}
                          >
                            {slot}
                            {(isStart || isEnd) && (
                              <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-gold" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {form.times.length === 1 && (
                      <p className="text-[11px] text-gold/80 mt-3" data-testid="min-hours-warning">
                        Please select at least 2 hours - pick an end time.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-details">
                <h2 className="font-serif text-3xl md:text-4xl mb-3">Tell us about you.</h2>
                <p className="text-white/60 mb-8">We'll keep your details private - always.</p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Full Name</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => updateForm({ name: e.target.value })}
                      placeholder="Jane Smith"
                      className="bg-deep-surface border-white/10 text-white input-lux"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Email</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateForm({ email: e.target.value })}
                      placeholder="jane@example.com"
                      className="bg-deep-surface border-white/10 text-white input-lux"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => updateForm({ phone: e.target.value })}
                      placeholder="04XX XXX XXX"
                      className="bg-deep-surface border-white/10 text-white input-lux"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Participant Type</Label>
                    <Select value={form.participantType} onValueChange={(v) => updateForm({ participantType: v })}>
                      <SelectTrigger className="bg-deep-surface border-white/10 text-white" data-testid="select-participant">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent className="bg-deep-elevated border-white/10 text-white">
                        <SelectItem value="ndis">NDIS Participant</SelectItem>
                        <SelectItem value="aged-care">Aged Care</SelectItem>
                        <SelectItem value="private">Privately Funded</SelectItem>
                        <SelectItem value="family">Family/Carer Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs tracking-[0.2em] uppercase text-gold mb-2 block">Anything we should know?</Label>
                    <Textarea
                      value={form.notes}
                      onChange={(e) => updateForm({ notes: e.target.value })}
                      placeholder="Preferred contact method, specific goals, accessibility needs..."
                      rows={4}
                      className="bg-deep-surface border-white/10 text-white input-lux resize-none"
                      data-testid="input-notes"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="step-confirm">
                <h2 className="font-serif text-3xl md:text-4xl mb-3">Review & Confirm</h2>
                <p className="text-white/60 mb-8">Looks right? Hit confirm and we'll be in touch.</p>
                <div className="space-y-4 rounded-2xl bg-deep-surface border border-white/5 p-6">
                  {[
                    ["Service", form.service],
                    ["Date", form.date ? format(form.date, "PPP") : "-"],
                    ["Hours", formattedTimeRange()],
                    ["Name", form.name],
                    ["Email", form.email],
                    ["Phone", form.phone],
                    ["Type", form.participantType],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between items-start gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
                      <span className="text-xs uppercase tracking-[0.15em] text-white/50">{k}</span>
                      <span className="text-white text-sm text-right">{v || "-"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nav buttons */}
            <div className="mt-10 flex items-center justify-between">
              <button
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-gold disabled:opacity-30"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              {step < 4 ? (
                <button
                  onClick={() => setStep((s) => Math.min(4, s + 1))}
                  disabled={!canNext()}
                  className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-xs font-semibold tracking-[0.15em] uppercase text-deep disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gold-light transition-all"
                  data-testid="next-btn"
                >
                  Continue
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="btn-gold inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-xs font-semibold tracking-[0.15em] uppercase text-deep disabled:opacity-60 hover:bg-gold-light transition-all"
                  data-testid="confirm-btn"
                >
                  {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>) : (<><Moon className="w-4 h-4" /> Confirm Booking</>)}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
